import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

const SOURCE_LABELS = {
  auto: 'Auto Search',
  single: 'Single Query',
  scheduled: 'Scheduled',
};

const STATUS_COLORS = {
  running: 'primary',
  completed: 'success',
  failed: 'error',
  pending: 'default',
  skipped: 'default',
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatUsd = (value) => {
  const amount = Number(value || 0);
  return amount > 0 ? `$${amount.toFixed(4)}` : '—';
};

const getElapsedTime = (startTime, endTime) => {
  if (!startTime) return '—';
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const elapsed = end - start;

  const hours = Math.floor(elapsed / 3600000);
  const minutes = Math.floor((elapsed % 3600000) / 60000);
  const seconds = Math.floor((elapsed % 60000) / 1000);

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const SearchProgressDetailsDialog = ({ open, runId, onClose }) => {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');

  const fetchRunDetails = async () => {
    if (!runId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/search-runs/${runId}`
      );
      setRun(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load search run details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && runId) {
      fetchRunDetails();
    }
  }, [open, runId]);

  useEffect(() => {
    if (!open || !runId || run?.status !== 'running') return undefined;

    const interval = setInterval(fetchRunDetails, 4000);
    return () => clearInterval(interval);
  }, [open, runId, run?.status]);

  const filteredQueryItems = (run?.queryItems || []).filter((item) => {
    if (!searchFilter) return true;
    const term = searchFilter.toLowerCase();
    return (
      item.link?.toLowerCase().includes(term) ||
      item.category?.toLowerCase().includes(term)
    );
  });

  const categoryStats = run?.categorySummary || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Search Progress Details
        {run && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={SOURCE_LABELS[run.source] || run.source} />
            <Chip size="small" label={run.status} color={STATUS_COLORS[run.status] || 'default'} />
            <Chip size="small" label={`${run.totalJobsFound} jobs found`} color="success" variant="outlined" />
            {(run.costs?.openaiUsd || 0) > 0 && (
              <Chip
                size="small"
                label={`OpenAI ${formatUsd(run.costs.openaiUsd)}`}
                color="info"
                variant="outlined"
              />
            )}
            {(run.costs?.scrapingdogUsd || 0) > 0 && (
              <Chip
                size="small"
                label={`ScrapingDog ${formatUsd(run.costs.scrapingdogUsd)}`}
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && !run && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        {run && (
          <>
            <Box sx={{ mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={run.progress?.percentage || 0}
                sx={{ height: 10, borderRadius: 1, mb: 1 }}
                color={run.status === 'failed' ? 'error' : 'primary'}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {run.progress?.current || 0} / {run.progress?.total || 0} queries
                  {run.failedQueries > 0 && ` · ${run.failedQueries} failed`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {getElapsedTime(run.startedAt, run.completedAt)}
                </Typography>
              </Box>
            <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Started: {formatDateTime(run.startedAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ended: {formatDateTime(run.completedAt)}
                </Typography>
                {(run.costs?.openaiUsd || 0) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    OpenAI: {formatUsd(run.costs.openaiUsd)}
                  </Typography>
                )}
                {(run.costs?.scrapingdogUsd || 0) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ScrapingDog: {formatUsd(run.costs.scrapingdogUsd)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ mb: 2 }}>
              <Tab label="By Category" />
              <Tab label={`Queries (${run.queryItems?.length || 0})`} />
            </Tabs>

            {detailTab === 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Queries</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Jobs Found</TableCell>
                      <TableCell align="right">Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryStats.map((cat) => (
                      <TableRow key={cat.category}>
                        <TableCell>{cat.category}</TableCell>
                        <TableCell align="right">{cat.total}</TableCell>
                        <TableCell align="right">{cat.completed}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={cat.jobsFound}
                            color={cat.jobsFound > 0 ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {cat.total > 0 ? Math.round((cat.completed / cat.total) * 100) : 0}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {categoryStats.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No category data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {detailTab === 1 && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter by query or category..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Query</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Date Filter</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Jobs</TableCell>
                        <TableCell align="right">OpenAI</TableCell>
                        <TableCell align="right">ScrapingDog</TableCell>
                        <TableCell>Started</TableCell>
                        <TableCell>Ended</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredQueryItems.map((item, index) => (
                        <TableRow key={`${item.link}-${item.afterDate}-${index}`}>
                          <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Typography variant="body2" noWrap title={item.link}>
                              {item.link}
                            </Typography>
                            {item.error && (
                              <Typography variant="caption" color="error" display="block">
                                {item.error}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>{item.afterDate || 'Anytime'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={item.status}
                              color={STATUS_COLORS[item.status] || 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">{item.jobsFound ?? 0}</TableCell>
                          <TableCell align="right">{formatUsd(item.costs?.openaiUsd)}</TableCell>
                          <TableCell align="right">{formatUsd(item.costs?.scrapingdogUsd)}</TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDateTime(item.startedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDateTime(item.completedAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredQueryItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No queries match your filter
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={fetchRunDetails} disabled={loading}>
          Refresh
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SearchProgressDetailsDialog;
